import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import LoadingSpinner from "src/components/LoadingSpinner";
import Tick from "./Tick";

export enum ButtonVariant {
  primary = "primary",
  gray = "gray",
  secondary = "secondary",
  danger = "danger",
  primarySquare = "primarySquare",
  whiteSquare = "whiteSquare",
  graySquare = "graySquare",
  dangerTextButton = "dangerTextButton",
  primaryTextButton = "primaryTextButton",
}

export const commonButtonStyles = `
  border-radius: 20px;
  padding: 9px 30px;
  font-weight: 600;
  font-size: var(--textM);
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
  // so we can change the border color for other button variations and
  // it will be the same size
  border: 1px solid transparent;
`;

const commonSquareButtonStyles = `
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 500;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
`;

const commonTextButtonStyles = `
    padding: 6px 16px;
    background-color: transparent;
    border-color: transparent;

    &:hover:enabled {
      text-decoration: underline;
    }
`;

const primaryButtonStyles = `
  background-color: var(--sea);
  color: var(--white);

  &:hover:enabled {
    background-color: var(--darkSea);
    color: var(--white); // mainly for LinkButton
  }

  &:hover:disabled {
    background-color: var(--sea);
    color: var(--white);
  }
`;
const secondaryButtonStyles = `
    background-color: transparent;
    border-color: var(--sea);
    color: var(--sea);
    border: 2px solid;

    &:hover {
      border-color: var(--darkSea);
      color: var(--darkSea);
    }

    &:hover:disabled {
      background-color: white;
      color: var(--sea);
    }
`;
const grayButtonStyles = `
    background-color: var(--foam);
    border-color: var(--foam);
    color: var(--husk);

    &:hover:enabled {
      background-color: var(--darkSand);
      border-color: var(--darkSand);
      color: var(--husk);
    }

    &:hover:disabled {
      background-color: var(--foam);
      border-color: var(--foam);
      color: var(--husk);
    }
`;

const whiteButtonStyles = `
  background-color: var(--white);
  color: var(--husk);
  border-color: var(--foam);

  &:hover:enabled {
    background-color: var(--cream);
    border-color: var(--grey01);
    color: var(--husk);
  }

  &:hover:disabled {
    background-color: var(--white);
    color: var(--husk);
    border-color: var(--foam);
  }
`;

const dangerButtonStyles = `
    background-color: var(--red);
    border-color: var(--red);
    color: var(--white);

    &:hover:enabled {
      background-color: var(--darkWatermelon);
      border-color: var(--darkWatermelon);
      color: var(--white);
    }

    &:hover:disabled {
      background-color: var(--red);
      border-color: var(--red);
      color: var(--white);
    }
`;

const dangerTextButtonStyles = `
    ${commonTextButtonStyles}
    color: var(--darkWatermelon);
`;

const primaryTextButtonStyles = `
    ${commonTextButtonStyles}
    color: var(--sea);
`;

export const primarySquareButtonStyles = `
  ${commonSquareButtonStyles}
  ${primaryButtonStyles}
`;

const whiteSquareButtonStyles = `
  ${commonSquareButtonStyles}
  ${whiteButtonStyles}
`;

const graySquareButtonStyles = `
    ${commonSquareButtonStyles}
    ${grayButtonStyles}
`;

const Container = styled.button<{
  isLoading: boolean;
  isSuccess: boolean;
  variant: ButtonVariant;
}>`
  ${commonButtonStyles}

  &:disabled {
    opacity: ${(props) =>
      props.isLoading || props.isSuccess
        ? 1
        : 0.65}; // is disabled because is loading or success, don't make translucent
  }

  ${(props) => props.variant === ButtonVariant.primary && primaryButtonStyles}
  ${(props) => props.variant === ButtonVariant.gray && grayButtonStyles}
  ${(props) => props.variant === ButtonVariant.danger && dangerButtonStyles}
  ${(props) =>
    props.variant === ButtonVariant.dangerTextButton && dangerTextButtonStyles}
  ${(props) =>
    props.variant === ButtonVariant.primaryTextButton &&
    primaryTextButtonStyles}
  ${(props) =>
    props.variant === ButtonVariant.secondary && secondaryButtonStyles}
  ${(props) =>
    props.variant === ButtonVariant.primarySquare && primarySquareButtonStyles}
  ${(props) =>
    props.variant === ButtonVariant.whiteSquare && whiteSquareButtonStyles}
  ${(props) =>
    props.variant === ButtonVariant.graySquare && graySquareButtonStyles}
`;

const ChildContent = styled.span<{
  isLoading: boolean;
  isSuccess: boolean;
}>`
    display: flex;
    align-items: center;
    opacity: ${(props) => (props.isLoading || props.isSuccess ? 0 : 1)};
  }
`;

const InnerContainer = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  // ensure content is always centered
  display: grid;
  place-items: center;
  place-content: center;

  & > * {
    grid-area: 1 / 1;
  }
`;

type ButtonProps = {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  type?: "button" | "submit" | "reset" | undefined;
  disabled?: boolean;
  hidden?: boolean;
  isLoading?: boolean;
  isSuccess?: boolean;
  className?: string;
  enableLoadingAndOrSuccessStates?: boolean;
  disableWidthLayoutLogic?: boolean;
  variant?: ButtonVariant;
  fixedWidthPx?: number;
  onDrop?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragEnter?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
};

export const AnchorButton = styled.a<{
  variant?: ButtonVariant;
}>`
  ${commonButtonStyles}

  ${(props) => props.variant === ButtonVariant.primary && primaryButtonStyles}
  ${(props) => props.variant === ButtonVariant.gray && grayButtonStyles}
  ${(props) => props.variant === ButtonVariant.danger && dangerButtonStyles}
  ${(props) =>
    props.variant === ButtonVariant.secondary && secondaryButtonStyles}
  ${(props) =>
    props.variant === ButtonVariant.primarySquare && primarySquareButtonStyles}
  ${(props) =>
    props.variant === ButtonVariant.whiteSquare && whiteSquareButtonStyles}
  ${(props) =>
    props.variant === ButtonVariant.graySquare && graySquareButtonStyles}

  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
`;

// inner container, so we can safely use flexbox etc
const Button = ({
  children,
  type,
  isLoading = false,
  isSuccess = false,
  disabled = false,
  hidden = false,
  className,
  // `enableLoadingAndOrSuccessStates` switches on/off the "make sure the
  // button size doesn't change when going into the loading or success
  // states" logic
  enableLoadingAndOrSuccessStates = false,
  disableWidthLayoutLogic = false,
  onClick,
  onDrop,
  onDragOver,
  onDragEnter,
  onDragLeave,
  variant,
  fixedWidthPx,
  ...rest
}: ButtonProps) => {
  const [width, setWidth] = useState(fixedWidthPx || 0);
  const [height, setHeight] = useState(0);
  const ref = useRef<HTMLButtonElement>(null);

  // Save the dimensions here
  // N.B. this won't allow the button to resize if the window is resized - i.e. this solution
  // isn't responsive. If this becomes a problem, need to add some debounced resize logic,
  // however it's tricky - what if the user resizes the page during the loading state?
  // An easy fix to this issue, required for the Login page, is disabling the width
  // fixing logic, which allows the button to be horizontally responsive (its width is
  // 100% anyway, so it is *meant* to resize to fill its parent).
  useEffect(
    () => {
      if (enableLoadingAndOrSuccessStates) {
        if (
          !disableWidthLayoutLogic &&
          ref?.current?.getBoundingClientRect()?.width &&
          !fixedWidthPx
        ) {
          setWidth(ref.current.getBoundingClientRect().width);
        }
        if (ref?.current?.getBoundingClientRect()?.height) {
          setHeight(ref.current.getBoundingClientRect().height);
        }
      }
    },
    // children are a dep so dimensions are updated if initial contents change
    [
      children,
      enableLoadingAndOrSuccessStates,
      disableWidthLayoutLogic,
      fixedWidthPx,
    ]
  );
  return (
    <Container
      ref={ref}
      style={{
        ...(width && { width: `${width}px` }),
        ...(height && { height: `${height}px` }),
      }}
      type={type}
      isLoading={isLoading}
      isSuccess={isSuccess}
      disabled={disabled}
      hidden={hidden}
      className={className || ""}
      variant={variant || ButtonVariant.primary}
      onClick={onClick}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      {...rest}
    >
      <InnerContainer>
        <ChildContent isLoading={isLoading} isSuccess={isSuccess}>
          {children}
        </ChildContent>
        {isLoading && <LoadingSpinner color="currentColor" />}
        {isSuccess && <Tick />}
      </InnerContainer>
    </Container>
  );
};

export default Button;
